'use client';
import React, { useState } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';

export interface JobFilters {
  search: string;
  region: string;
  licenseCode: string;
  pdpRequired: string;
  jobType: string;
  salaryMin: string;
}

interface Props {
  onFiltersChange?: (f: JobFilters) => void;
}

export default function JobFiltersBar({ onFiltersChange }: Props) {
  const [filters, setFilters] = useState<JobFilters>({
    search: '',
    region: 'All',
    licenseCode: 'All',
    pdpRequired: 'All',
    jobType: 'All',
    salaryMin: 'Any',
  });

  const update = (key: keyof JobFilters, value: string) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    onFiltersChange?.(next);
  };

  return (
    <div className="bg-card rounded-2xl border border-border card-shadow p-4">
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={filters.search}
            onChange={(e) => update('search', e.target.value)}
            placeholder="Search jobs, companies, or locations..."
            className="input-field pl-9 py-2.5 text-sm w-full"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <SlidersHorizontal size={13} />
            <span className="font-600">Filters:</span>
          </div>
          <select
            value={filters.region}
            onChange={(e) => update('region', e.target.value)}
            className="input-field py-2.5 text-sm w-36"
          >
            <option value="All">All Regions</option>
            <option value="SA">South Africa</option>
            <option value="International">International</option>
          </select>
          <select
            value={filters.licenseCode}
            onChange={(e) => update('licenseCode', e.target.value)}
            className="input-field py-2.5 text-sm w-36"
          >
            <option value="All">All Licenses</option>
            <option value="Code 8">Code 8</option>
            <option value="Code 10">Code 10</option>
            <option value="Code 14">Code 14</option>
            <option value="Code EC">Code EC</option>
          </select>
          <select
            value={filters.pdpRequired}
            onChange={(e) => update('pdpRequired', e.target.value)}
            className="input-field py-2.5 text-sm w-36"
          >
            <option value="All">PDP: Any</option>
            <option value="Yes">PDP Required</option>
            <option value="No">No PDP Needed</option>
          </select>
          <select
            value={filters.jobType}
            onChange={(e) => update('jobType', e.target.value)}
            className="input-field py-2.5 text-sm w-40"
          >
            <option value="All">All Job Types</option>
            <option value="Long-Haul">Long-Haul</option>
            <option value="Local Distribution">Local Distribution</option>
            <option value="Mining">Mining</option>
            <option value="Tanker">Tanker</option>
            <option value="Bus / Coach">Bus / Coach</option>
            <option value="Refrigerated">Refrigerated</option>
          </select>
          <select
            value={filters.salaryMin}
            onChange={(e) => update('salaryMin', e.target.value)}
            className="input-field py-2.5 text-sm w-40"
          >
            <option value="Any">Any Salary</option>
            <option value="10000">R10,000+/mo</option>
            <option value="15000">R15,000+/mo</option>
            <option value="20000">R20,000+/mo</option>
            <option value="25000">R25,000+/mo</option>
          </select>
        </div>
      </div>
    </div>
  );
}